/**
 * Pure formatting + tier helpers for SegmentCard.
 *
 * Kept free of React so SegmentCard stays under its line budget and so these
 * helpers can be unit-tested directly without rendering.
 */

import type { ScoringFieldKey } from '@ispot/shared'

export type ScoreTier = 'high' | 'mid' | 'low' | 'none'

export const SCORE_KEYS: { key: ScoringFieldKey; label: string }[] = [
  { key: 'strategicFit', label: 'Fit' },
  { key: 'marketSize', label: 'Size' },
  { key: 'marketGrowth', label: 'Growth' },
  { key: 'easyToServe', label: 'Serve' },
  { key: 'easyToCompete', label: 'Compete' },
  { key: 'profitPotential', label: 'Profit' },
]

export function tierFor(value: number | null | undefined): ScoreTier {
  if (value == null || value === 0) return 'none'
  if (value >= 7) return 'high'
  if (value >= 4) return 'mid'
  return 'low'
}

export function tierBg(t: ScoreTier): string {
  if (t === 'high') return 'var(--color-market-500, #2BBCC7)'
  if (t === 'mid') return 'var(--color-solution, #FFC05F)'
  if (t === 'low') return '#d4948a'
  return 'var(--color-warm-300)'
}

export function tierSurface(t: ScoreTier): string {
  if (t === 'high') return 'var(--color-market-50, #f0f7f2)'
  if (t === 'mid') return 'var(--color-solution-50, #fff7e7)'
  if (t === 'low') return 'var(--color-danger-bg, #fbdede)'
  return 'var(--color-warm-100)'
}

export function tierStroke(t: ScoreTier): string {
  return t === 'none' ? 'var(--color-warm-300)' : tierBg(t)
}

/**
 * Darker variant of tier colors, suitable for display text over neutral
 * backgrounds where tierBg (a surface color) would lack contrast.
 */
export function tierFg(t: ScoreTier): string {
  if (t === 'high') return 'var(--color-market-700, #15747a)'
  if (t === 'mid') return 'var(--color-solution-700, #946818)'
  if (t === 'low') return '#a93131'
  return 'var(--color-text-muted)'
}

export function formatMoney(v: number | null | undefined): string {
  if (v == null) return '—'
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(0)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`
  return `$${v}`
}

export function formatPercent(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${v.toFixed(0)}%`
}

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function formatRelative(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const diff = now - t
  const day = 86_400_000
  if (diff < day) return 'today'
  if (diff < 2 * day) return 'yesterday'
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))}w ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
